import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { PostSubscriptionActionEnum } from 'app/shared/enums/post-subscription-action.enum';
import { RTCEvent } from 'app/shared/enums/rtc-event.enum';
import { ChannelNameHelperService } from 'app/shared/services/message-bus/channel-name-helper.service';
import {
  MESSAGE_BUS_SERVICE, MessageBusService
} from 'app/shared/services/message-bus/message-bus-service.interface';
import { RealTimeEvent } from 'app/shared/services/message-bus/real-time-event.model';
import { GameShowRunClosedEvent } from 'app/student/gameshow/models/gameshow-run-closed-event';
import { GameShowRunLaunchedEvent } from 'app/student/gameshow/models/gameshow-run-launched-event';
import { FeedbackService } from 'app/teacher/gameshow/feedback/feedback.service';
import { FeedbackReceived } from 'app/teacher/gameshow/feedback/models/feedback-received.model';
import { GameShowService } from 'app/teacher/gameshow/services/gameshow.service';

import { GameShowRepository } from './gameshow-repository.interface';
import { GameShowStatInfo } from './models/gameshow-stat-info';
import { TeacherGameShowInfo } from './models/teacher-gameshow-info.model';
import { TeacherGameShowQuestionChoice } from './models/teacher-gameshow-question-choice.model';

@Injectable({
  providedIn: 'root'
})
export class KhGameShowStorageService implements GameShowRepository {
  private offSet = 0;
  public constructor(
    @Inject(MESSAGE_BUS_SERVICE) private readonly messageBusService: MessageBusService,
    private readonly gameShowService: GameShowService,
    private readonly feedbackService: FeedbackService) {
    this.getBrowserOffset();
  }

  private getBrowserOffset() {
    this.gameShowService.getDateLive()
      .pipe(first())
      .subscribe(serverTime => {
        const browserTime = Date.now();

        this.offSet = (serverTime - browserTime) * -1;
      });
  }

  public getDate(): Date {
    const adjustedDate = Date.now() + this.offSet;

    return new Date(adjustedDate);
  }

  public getFeedback(classId: string): Observable<FeedbackReceived> {
    const channel = ChannelNameHelperService.getGameShowForTeacherName(classId);

    return this.messageBusService.connect(channel)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.FeedbackReceived),
        map((rtEvent: RealTimeEvent) => rtEvent.messageObject as FeedbackReceived));
  }

  public getGameShow(classId: string): Observable<any> {
    const channel = ChannelNameHelperService.getGameShowForClassName(classId);

    return this.messageBusService.connect(channel)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.GameShowUpdated),
        map((rtEvent: RealTimeEvent) => rtEvent.messageObject as any));
  }

  public getPublicGameShow(classId: string): Observable<GameShowStatInfo | null> {
    const channel = ChannelNameHelperService.getGameShowForClassName(classId);

    return this.messageBusService.connect(channel, PostSubscriptionActionEnum.FindPreSubGameShowState)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.GameShowUpdated),
        map((rtEvent: RealTimeEvent) => rtEvent.messageObject as GameShowStatInfo));
  }

  public getTeacherGameShowLive(classId: string, gameShowRunId: string, attemptNumber: number): Observable<TeacherGameShowInfo | null> {
    const channel = ChannelNameHelperService.getGameShowForTeacherName(classId);

    const initialState = this.gameShowService.getTeacherGameShowLiveState(gameShowRunId, attemptNumber);

    const reconnectObservable = this.gameShowService.getTeacherGameShowLiveState(gameShowRunId, attemptNumber);

    const webSocketUpdates = this.messageBusService.connect(channel, '', reconnectObservable)
      .pipe(
        map((rtEvent: RealTimeEvent) => {
          const updateEvent = { EventType: rtEvent.name, Update: {} as any };
          switch (rtEvent.name) {
            case RTCEvent.StudentAnswered:
            case RTCEvent.StudentJoined:
            case RTCEvent.StudentLeft:
            case RTCEvent.QuestionClosed:
            case RTCEvent.QuestionChoicesReceived:
            case RTCEvent.StudentSolution:
            case RTCEvent.DeleteStudentSolution:
            case RTCEvent.FeedbackReceived:
              updateEvent.Update = rtEvent.messageObject;
              break;
            default:
              return rtEvent.messageObject as any;
          }

          return updateEvent;
        }));

    return combineLatest([initialState, webSocketUpdates])
      .pipe(
        map(([initialResult, wsEvents]) => ({ ...initialResult, ...wsEvents } as unknown as TeacherGameShowInfo))
      );
  }

  public disconnectChannel(classId: string): void {
    const channel = ChannelNameHelperService.getGameShowForTeacherName(classId);
    this.messageBusService.disconnect(channel);
  }

  public getQuestionChoices(classId: string): Observable<TeacherGameShowQuestionChoice[] | null> {
    const channel = ChannelNameHelperService.getGameShowForTeacherName(classId);

    return this.messageBusService.connect(channel)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.GameShowUpdated),
        map((rtEvent: RealTimeEvent) => rtEvent.messageObject as any));
  }

  public getGameShowRunId(classId: string): Observable<string | null> {
    const channel = ChannelNameHelperService.getClassesChannelName(classId);

    return this.messageBusService.connect(channel, PostSubscriptionActionEnum.FindPreSubGameShowRun)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.GameShowLaunched || e.name === RTCEvent.GameShowUpdated),
        map((rtEvent: RealTimeEvent) => {
          if (rtEvent.messageObject) {
            // Handle GameShowLaunched event
            if (rtEvent.name === RTCEvent.GameShowLaunched) {
              return (rtEvent.messageObject as GameShowRunLaunchedEvent).gameShowRunId;
            }
            // Handle GameShowUpdated event (for close)
            if (rtEvent.name === RTCEvent.GameShowUpdated && (rtEvent.messageObject as GameShowRunClosedEvent).isGameShowRunClosed) {
              return null; // Gameshow closed, clear the ID
            }
          }

          return null;
        }));
  }

  public getRejectedStudents(classId: string): Observable<Record<string, any> | null> {
    const channel = ChannelNameHelperService.getGameShowForClassName(classId);

    return this.messageBusService.connect(channel, PostSubscriptionActionEnum.FindIfIAmRejected)
      .pipe(
        filter(e => e.id !== null),
        filter(e => e.name === RTCEvent.StudentRejected),
        map((rtEvent: RealTimeEvent) => rtEvent.messageObject as any));
  }
}
